import { CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { useErrorTranslator } from '../../../errors/translate';

import { Button } from '../../../components/Shared/Button';
import { Badge } from '../../../components/Shared/Badge';
import { Modal } from '../../../components/Shared/Modal';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from '../../../components/Shared/Table';
import type {
  FacilityFlowCoreModulesInvoicesWorkflowsBulkCreateInvoicesResponse,
} from '../../../api/generated/models';

export type BulkResponse =
  FacilityFlowCoreModulesInvoicesWorkflowsBulkCreateInvoicesResponse;

interface BulkResultModalProps {
  response: BulkResponse;
  /** Proposal rows the request was built from — only used to resolve object names. */
  rows: Array<{ objectId?: string; objectName?: string | null }>;
  onClose: () => void;
  onOpenCreated: () => void;
}

/** Per-object outcome of a bulk run (monthly run and standing invoices share the response shape). */
export function BulkResultModal({
  response, rows, onClose, onOpenCreated,
}: BulkResultModalProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const translateError = useErrorTranslator();

  const results = response.results || [];
  const successes = results.filter((r) => r.invoiceId);
  const failures = results.filter((r) => !r.invoiceId);

  const objectName = (id?: string | null) => {
    if (!id) return '—';
    const row = rows.find((x) => x.objectId === id);
    return row?.objectName || id;
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('invoices:bulkResult.title')}
      size="lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <Button variant="secondary" onClick={onClose}>
            {t('invoices:bulkResult.close')}
          </Button>
          {successes.length > 0 && (
            <Button onClick={onOpenCreated}>
              {t('invoices:bulkResult.openCreated')}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
            {t('invoices:bulkResult.successCount', { count: successes.length })}
          </Badge>
          {failures.length > 0 && (
            <Badge variant="danger" className="flex items-center gap-1">
              <XCircle className="w-3.5 h-3.5" strokeWidth={2} />
              {t('invoices:bulkResult.failureCount', { count: failures.length })}
            </Badge>
          )}
        </div>

        <div className="border border-slate-200 rounded-md overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('invoices:bulkResult.objectColumn')}</TableHeader>
                <TableHeader>{t('invoices:fields.status')}</TableHeader>
                <TableHeader>{t('invoices:bulkResult.errorColumn')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r) => (
                <TableRow key={r.objectId}>
                  <TableCell className="text-[13px] text-slate-700">{objectName(r.objectId)}</TableCell>
                  <TableCell>
                    {r.invoiceId ? (
                      <Badge variant="success">
                        {t('invoices:status.draft')}
                      </Badge>
                    ) : (
                      <Badge variant="danger">
                        {t('common:errors.actionFailed')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px] text-red-600">
                    {/* why: the BE puts the stable error code next to its raw English
                        message; the code is an i18n key, the message only a fallback. */}
                    {r.errorCode
                      ? translateError({ code: r.errorCode, message: r.error ?? '' })
                      : (r.error || '')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </Modal>
  );
}
